import { Api, BASE_URL, BOTS_BASE_URL } from '@/api/index';
import { WeddingEventSettings } from '@/types/wedding';
import axios, { AxiosRequestConfig } from 'axios';
import { CreateWeddingEvent, WeddingEvent, WeddingEventImageComment } from 'ignite360-core';

export class WeddingApi {
  static async loadBotWeddingEvents(botId: string): Promise<WeddingEvent[]> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events`,
    });
    const result = await axios.request<WeddingEvent[]>(config);
    return result.data;
  }

  // create a new campaign
  static async createBotWeddingEvent(
    botId: string,
    event: CreateWeddingEvent,
  ): Promise<WeddingEvent> {
    const config = Api.finalizeConfig({
      method: 'POST',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events`,
      data: event,
    });
    const result = await axios.request<WeddingEvent>(config);
    return result.data;
  }

  static async getPublicWeddingEvent(
    eventId: string,
    userSessionId?: string,
  ): Promise<WeddingEvent> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BASE_URL}/public/wedding/events/${eventId}`,
      params: {
        userSessionId,
      },
    });
    const result = await axios.request<WeddingEvent>(config);
    return result.data;
  }

  static async getBotWeddingEvent(botId: string, eventId: string): Promise<WeddingEvent> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}`,
    });
    const result = await axios.request<WeddingEvent>(config);
    return result.data;
  }

  static async uploadMedia(botId: string, eventId: string, formData: FormData) {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}/media`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    return axios.request(Api.finalizeConfig(config));
  }

  // Public uploads go directly to S3: get a pre-signed form POST, upload, then complete.
  static async createPublicUpload(
    botId: string,
    eventId: string,
    data: { extension: string; contentType: string; withThumbnail: boolean },
  ): Promise<{
    imageId: string;
    url: string;
    fields: Record<string, string>;
    thumbnail?: { url: string; fields: Record<string, string> };
  }> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BOTS_BASE_URL}/${botId}/public/wedding/events/${eventId}/uploads`,
      data,
    };
    return (await axios.request(Api.finalizeConfig(config))).data;
  }

  static async completePublicUpload(
    botId: string,
    eventId: string,
    imageId: string,
    data: { extension: string; hasThumbnail: boolean; userSessionId: string },
  ) {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BOTS_BASE_URL}/${botId}/public/wedding/events/${eventId}/uploads/${imageId}/complete`,
      data,
    };
    return axios.request(Api.finalizeConfig(config));
  }

  static async deleteBotWeddingEvent(botId: string, eventId: string) {
    const config = Api.finalizeConfig({
      method: 'DELETE',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}`,
    });
    return axios.request(config);
  }

  static async updateBotWeddingEvent(botId: string, eventId: string, event: WeddingEventSettings) {
    const config = Api.finalizeConfig({
      method: 'PUT',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}`,
      data: event,
    });
    return axios.request(config);
  }

  static async deleteBotWeddingEventImage(botId: string, eventId: string, imageId: string) {
    const config = Api.finalizeConfig({
      method: 'DELETE',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}/images/${imageId}`,
    });
    return axios.request(config);
  }

  static async deletePublicBotWeddingEventImage(
    botId: string,
    eventId: string,
    imageId: string,
    userSessionId?: string,
  ) {
    const config = Api.finalizeConfig({
      method: 'DELETE',
      url: `${BOTS_BASE_URL}/${botId}/public/wedding/events/${eventId}/images/${imageId}`,
      params: {
        userSessionId,
      },
    });
    return axios.request(config);
  }

  static async updateBotWeddingEventImagesOrder(
    botId: string,
    eventId: string,
    updatedOrder: { id: string; order: number }[],
  ) {
    const config = Api.finalizeConfig({
      method: 'PUT',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}/images/order`,
      data: { updatedOrder },
    });
    return axios.request(config);
  }

  // Add this method to the WlcmHomeApi class

  static async updateImageHeroStatus(
    botId: string,
    eventId: string,
    imageId: string,
    heroImage: boolean,
  ) {
    const config = Api.finalizeConfig({
      method: 'PUT',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}/images/${imageId}/hero`,
      data: { heroImage },
    });
    return axios.request(config);
  }

  static async uploadAgentImage(botId: string, eventId: string, formData: FormData) {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}/agent-image`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    return axios.request(Api.finalizeConfig(config));
  }

  static async deleteAgentImage(botId: string, eventId: string) {
    const config = Api.finalizeConfig({
      method: 'DELETE',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}/agent-image`,
    });
    return axios.request(config);
  }

  static async updateImageState(botId: string, eventId: string, imageId: string, state: string) {
    const config = Api.finalizeConfig({
      method: 'PUT',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}/images/${imageId}/state`,
      data: { state },
    });
    return axios.request(config);
  }

  static async createPublicImageComment(
    eventId: string,
    imageId: string,
    comment: { name: string; comment: string },
    userSessionId?: string,
  ) {
    const config = Api.finalizeConfig({
      method: 'POST',
      url: `${BASE_URL}/public/wedding/events/${eventId}/images/${imageId}/comments`,
      data: comment,
      params: {
        userSessionId,
      },
    });
    return axios.request(config);
  }

  // static async getImageComments(
  //   botId: string,
  //   eventId: string,
  //   imageId: string,
  // ): Promise<WeddingEventImageComment[]> {
  //   const config = Api.finalizeConfig({
  //     method: 'GET',
  //     url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}/images/${imageId}/comments`,
  //   });
  //   const result = await axios.request<WeddingEventImageComment[]>(config);
  //   return result.data;
  // }

  // static async getPublicImageComments(
  //   eventId: string,
  //   imageId: string,
  //   userSessionId?: string,
  // ): Promise<WeddingEventImageComment[]> {
  //   const config = Api.finalizeConfig({
  //     method: 'GET',
  //     url: `${BASE_URL}/public/wedding/events/${eventId}/images/${imageId}/comments`,
  //     params: {
  //       userSessionId,
  //     },
  //   });
  //   const result = await axios.request<WeddingEventImageComment[]>(config);
  //   return result.data;
  // }

  // Sets the like to `liked`, safe to repeat (unlike toggleLike)
  static async setLike(eventId: string, imageId: string, userSessionId: string, liked: boolean) {
    const config = Api.finalizeConfig({
      method: 'POST',
      url: `${BASE_URL}/public/wedding/events/${eventId}/images/${imageId}/likes`,
      params: {
        userSessionId,
      },
      data: { liked },
    });
    return axios.request(config);
  }

  static async toggleLike(eventId: string, imageId: string, userSessionId?: string) {
    const config = Api.finalizeConfig({
      method: 'POST',
      url: `${BASE_URL}/public/wedding/events/${eventId}/images/${imageId}/likes`,
      params: {
        userSessionId,
      },
    });
    return axios.request(config);
  }

  static async updatePublicImageCaption(
    eventId: string,
    imageId: string,
    data: { caption: string },
    userSessionId?: string,
  ) {
    const config = Api.finalizeConfig({
      method: 'PUT',
      url: `${BASE_URL}/public/wedding/events/${eventId}/images/${imageId}/caption`,
      data,
      params: {
        userSessionId,
      },
    });
    return axios.request(config);
  }

  static async deletePublicComment(
    eventId: string,
    imageId: string,
    commentId: string,
    userSessionId?: string,
  ) {
    const config = Api.finalizeConfig({
      method: 'DELETE',
      url: `${BASE_URL}/public/wedding/events/${eventId}/images/${imageId}/comments/${commentId}`,
      params: {
        userSessionId,
      },
    });
    return axios.request(config);
  }

  static async approveComment(botId: string, eventId: string, imageId: string, commentId: string) {
    const config = Api.finalizeConfig({
      method: 'PUT',
      url: `${BOTS_BASE_URL}/${botId}/wedding/events/${eventId}/images/${imageId}/comments/${commentId}/approve`,
    });
    return axios.request(config);
  }
}
